import React, { useEffect, useState } from 'react';
import { Table, Modal, Form, Button, Badge, Spinner, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import client from '../api/client';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';

const empty = {
  name: '',
  email: '',
  phone: '',
  password: '',
  role: 'sales',
  commissionType: 'percent',
  commissionValue: 0,
  active: true,
};

const roleColor = { admin: 'danger', sales: 'primary', builder: 'success', support: 'warning' };

const Users = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const isAdmin = user.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (roleFilter) params.role = roleFilter;
      const { data } = await client.get('/users', { params });
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setEditing(null); setForm(empty); setShow(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...empty, ...it, password: '' });
    setShow(true);
  };
  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await client.put(`/users/${editing._id}`, payload);
        toast.success('User updated');
      } else {
        await client.post('/auth/register', form);
        toast.success('User created');
      }
      setShow(false);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.message || 'Save failed');
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    await client.delete(`/users/${id}`);
    toast.info('User deactivated');
    load();
  };

  return (
    <div>
      <PageHeader
        title="Users / Staff"
        subtitle="Admin · Sales Agents · Builders · Support"
        actionLabel={isAdmin ? 'New User' : null}
        onAction={openNew}
      />

      <div className="d-flex gap-2 mb-3">
        <InputGroup style={{ maxWidth: 280 }}>
          <Form.Control placeholder="Search name / email / phone" value={q} onChange={(e) => setQ(e.target.value)} />
        </InputGroup>
        <Form.Select style={{ maxWidth: 200 }} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">All roles</option>
          <option value="admin">Admin</option>
          <option value="sales">Sales</option>
          <option value="builder">Builder</option>
          <option value="support">Support</option>
        </Form.Select>
        <Button onClick={load}><i className="bi bi-search"></i></Button>
      </div>

      {loading ? <Spinner animation="border" /> : (
        <Table hover responsive>
          <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Commission</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-4">No users</td></tr>}
            {items.map((u) => (
              <tr key={u._id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td>{u.phone || '-'}</td>
                <td><Badge bg={roleColor[u.role] || 'secondary'} className="text-uppercase">{u.role}</Badge></td>
                <td>
                  {u.role === 'sales'
                    ? `${u.commissionValue} ${u.commissionType === 'percent' ? '%' : u.commissionType === 'per_sqft' ? '₹/sqft' : 'flat'}`
                    : '-'}
                </td>
                <td>{u.active ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>}</td>
                <td className="text-end">
                  {isAdmin && (
                    <>
                      <Button size="sm" variant="outline-primary" onClick={() => openEdit(u)}><i className="bi bi-pencil"></i></Button>{' '}
                      <Button size="sm" variant="outline-danger" onClick={() => deactivate(u._id)}><i className="bi bi-trash"></i></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={show} onHide={() => setShow(false)}>
        <Form onSubmit={save}>
          <Modal.Header closeButton><Modal.Title>{editing ? 'Edit User' : 'New User'}</Modal.Title></Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6"><Form.Label>Name *</Form.Label>
                <Form.Control required value={form.name} onChange={(e) => onChange('name', e.target.value)} /></div>
              <div className="col-md-6"><Form.Label>Email *</Form.Label>
                <Form.Control required type="email" value={form.email} onChange={(e) => onChange('email', e.target.value)} disabled={!!editing} /></div>
              <div className="col-md-6"><Form.Label>Phone</Form.Label>
                <Form.Control value={form.phone} onChange={(e) => onChange('phone', e.target.value)} /></div>
              <div className="col-md-6"><Form.Label>Role *</Form.Label>
                <Form.Select required value={form.role} onChange={(e) => onChange('role', e.target.value)}>
                  <option value="admin">Admin</option>
                  <option value="sales">Sales</option>
                  <option value="builder">Builder</option>
                  <option value="support">Support</option>
                </Form.Select></div>
              <div className="col-md-12"><Form.Label>{editing ? 'Reset Password (leave blank to keep)' : 'Password *'}</Form.Label>
                <Form.Control type="password" required={!editing} value={form.password} onChange={(e) => onChange('password', e.target.value)} /></div>
              {form.role === 'sales' && (
                <>
                  <div className="col-md-6"><Form.Label>Commission Type</Form.Label>
                    <Form.Select value={form.commissionType} onChange={(e) => onChange('commissionType', e.target.value)}>
                      <option value="percent">% of Booking Value</option>
                      <option value="per_sqft">₹ per sqft</option>
                      <option value="flat">Flat amount</option>
                    </Form.Select></div>
                  <div className="col-md-6"><Form.Label>Commission Value</Form.Label>
                    <Form.Control type="number" step="0.01" value={form.commissionValue} onChange={(e) => onChange('commissionValue', Number(e.target.value))} /></div>
                </>
              )}
              {editing && (
                <div className="col-12">
                  <Form.Check
                    type="switch"
                    label="Active"
                    checked={!!form.active}
                    onChange={(e) => onChange('active', e.target.checked)}
                  />
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShow(false)}>Cancel</Button>
            <Button type="submit" variant="primary">{editing ? 'Save' : 'Create'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
